import React, { useState, useEffect } from 'react';
import {
  Package,
  ShoppingCart,
  CheckCircle,
  XCircle,
  CreditCard,
  ArrowLeft
} from 'lucide-react';
import { useEnhancedCompany } from '../contexts/EnhancedCompanyContext';

// Import top-up service for dynamic loading
import { getAllTopups, getTopupCategories, getTopupsByCategory } from '../utils/topupService';

const TopupPurchase = ({ onClose }) => {
  const { currentCompany } = useEnhancedCompany();
  const companyId = currentCompany?.companyId || currentCompany?.id;
  const [selectedTopup, setSelectedTopup] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [topups, setTopups] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loadingTopups, setLoadingTopups] = useState(true);

  // Load top-ups on component mount
  useEffect(() => {
    loadTopups();
  }, []);

  const loadTopups = async () => {
    try {
      setLoadingTopups(true);
      const [topupsData, categoriesData] = await Promise.all([
        getAllTopups(),
        getTopupCategories()
      ]);
      setTopups(topupsData);
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error loading top-ups:', error);
      setMessage({ type: 'error', text: 'Failed to load top-up packages' });
    } finally {
      setLoadingTopups(false);
    }
  };

  const handlePurchaseClick = (topup) => {
    setSelectedTopup(topup);
    setShowPaymentModal(true);
  };

  const handlePayment = async (paymentMethod) => {
    if (!selectedTopup || !companyId) return;

    try {
      setLoading(true);
      setMessage({ type: '', text: '' });

      // Prepare payment metadata
      const metadata = {
        companyId: companyId,
        type: 'topup',
        topupId: selectedTopup.id,
        topupName: selectedTopup.name
      };

      // Initialize payment based on method
      if (paymentMethod === 'razorpay') {
        await initializeRazorpayPayment(selectedTopup.price, metadata);
      } else if (paymentMethod === 'stripe') {
        await initializeStripePayment(selectedTopup.price, metadata);
      }

      setMessage({
        type: 'success',
        text: 'Payment initiated successfully!'
      });

    } catch (error) {
      console.error('Error initiating payment:', error);
      setMessage({
        type: 'error',
        text: `Error: ${error.message}`
      });
    } finally {
      setLoading(false);
    }
  };

  const initializeRazorpayPayment = async (amount, metadata) => {
    // TODO: Implement actual Razorpay integration
    console.log('Razorpay payment:', { amount, metadata });
    
    // Placeholder: Show alert
    alert(`Razorpay payment for ₹${amount} will be processed here`);
  };

  const initializeStripePayment = async (amount, metadata) => {
    // TODO: Implement actual Stripe integration
    console.log('Stripe payment:', { amount, metadata });
    
    // Placeholder: Show alert
    alert(`Stripe payment for ₹${amount} will be processed here`);
  };

  const formatLimits = (limits) => {
    const parts = [];
    if (limits.firebase_operations > 0) parts.push(`${limits.firebase_operations.toLocaleString()} operations`);
    if (limits.storage_mb > 0) {
      const storageGB = limits.storage_mb >= 1024 ? `${(limits.storage_mb / 1024).toFixed(1)} GB` : `${limits.storage_mb} MB`;
      parts.push(`${storageGB} storage`);
    }
    return parts.join(', ');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={onClose}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>
          <div className="flex items-center gap-3 mb-2">
            <Package className="w-8 h-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-800">Buy Top-up</h1>
          </div>
          <p className="text-gray-600">Boost your limits with one-time top-up packages</p>
        </div>

        {/* Message Display */}
        {message.text && (
          <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
            message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}>
            {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
            <span>{message.text}</span>
          </div>
        )}

        {/* Loading State */}
        {loadingTopups ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading top-up packages...</p>
            </div>
          </div>
        ) : (
          /* Top-up Packages by Category */
          categories.map(category => {
            const categoryTopups = topups.filter(topup => topup.category === category);
            if (categoryTopups.length === 0) return null;
            
            return (
              <div key={category} className="mb-8">
                <h3 className="text-xl font-bold text-gray-800 mb-4 capitalize">
                  {category} Top-ups
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {categoryTopups.map(topup => (
            <div
              key={topup.id}
              className={`bg-white rounded-xl shadow-lg p-6 border-2 transition-all hover:shadow-xl ${
                topup.popular ? 'border-purple-500' : 'border-gray-200'
              }`}
            >
              {topup.popular && (
                <div className="inline-block px-3 py-1 bg-purple-500 text-white text-xs font-bold rounded-full mb-3">
                  POPULAR
                </div>
              )}

              <h3 className="text-xl font-bold text-gray-800 mb-2">{topup.name}</h3>
              <p className="text-gray-600 text-sm mb-4">{topup.description}</p>

              <div className="bg-gradient-to-br from-blue-50 to-purple-50 p-4 rounded-lg mb-4">
                <div className="text-3xl font-bold text-blue-700 mb-1">
                  {topup.currency}{topup.price}
                </div>
                <div className="text-sm text-gray-600">One-time purchase, till-use</div>
              </div>

              <div className="text-sm text-gray-700 mb-4">
                {formatLimits(topup.limits)}
              </div>

              <button
                onClick={() => handlePurchaseClick(topup)}
                disabled={loading}
                className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white py-3 rounded-lg font-bold hover:from-blue-600 hover:to-purple-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <ShoppingCart className="w-5 h-5" />
                Buy Now
              </button>
            </div>
                  ))}
                </div>
              </div>
            );
          })
        )}

        {/* Payment Modal */}
        {showPaymentModal && selectedTopup && (
          <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-[60] p-0 sm:p-4" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
            <div className="bg-white rounded-t-2xl sm:rounded-xl shadow-2xl max-w-md w-full p-4 sm:p-6 max-h-[92dvh] overflow-y-auto">
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Complete Purchase</h3>

              <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                <div className="font-semibold text-gray-800 mb-2">{selectedTopup.name}</div>
                <div className="text-sm text-gray-600 mb-2">{selectedTopup.description}</div>
                <div className="text-2xl font-bold text-blue-700">
                  {selectedTopup.currency}{selectedTopup.price}
                </div>
              </div>

              <p className="text-sm text-gray-600 mb-4">
                Select your payment method:
              </p>

              <div className="space-y-3 mb-6">
                <button
                  onClick={() => handlePayment('razorpay')}
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white py-3 rounded-lg font-semibold hover:from-blue-600 hover:to-blue-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <CreditCard className="w-5 h-5" />
                  Pay with Razorpay
                </button>

                <button
                  onClick={() => handlePayment('stripe')}
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-purple-500 to-purple-600 text-white py-3 rounded-lg font-semibold hover:from-purple-600 hover:to-purple-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <CreditCard className="w-5 h-5" />
                  Pay with Stripe
                </button>
              </div>

              <button
                onClick={() => {
                  setShowPaymentModal(false);
                  setSelectedTopup(null);
                }}
                className="w-full px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TopupPurchase;



